import { type ReactNode } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Box, RadioGroup, RadioButton, Stack } from '@eneco/sparky';
import { Control, Controller, Path } from 'react-hook-form';

import { EnergyUsagePurposeContentField, EnergyUsagePurposeListField } from './types';

interface FormFieldsWithEnergyUsagePurpose {
  energyUsagePurpose?: string;
}

interface EnergyUsagePurposeSelectorProps<
  T extends FormFieldsWithEnergyUsagePurpose = FormFieldsWithEnergyUsagePurpose,
> {
  control: Control<T>;
  energyUsagePurposeList: EnergyUsagePurposeListField;
  content?: EnergyUsagePurposeContentField;
  direction?: 'row' | 'column';
}

const toRadioGroupValue = (value?: string) => (value && value !== 'none' ? value : '');

const EnergyUsagePurposeSelector = <T extends FormFieldsWithEnergyUsagePurpose = FormFieldsWithEnergyUsagePurpose>({
  control,
  energyUsagePurposeList,
  content,
  direction = 'row',
}: EnergyUsagePurposeSelectorProps<T>) => {
  if (!energyUsagePurposeList?.value?.options?.length) {
    return null;
  }

  const hintFromList = energyUsagePurposeList.value.hint;
  const contentHtml = content?.value;
  const hint: ReactNode =
    contentHtml || hintFromList ? (
      <>
        {contentHtml ? <RichText html={contentHtml} /> : null}
        {hintFromList}
      </>
    ) : undefined;

  return (
    <Box paddingTop="3">
      <Stack direction="column" gap="3">
        <Controller
          control={control}
          name={'energyUsagePurpose' as Path<T>}
          render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { error } }) => (
            <div onBlur={onBlur}>
              <RadioGroup
                ref={ref}
                name={name}
                direction={direction}
                value={toRadioGroupValue(value as string)}
                onValueChange={onChange as (nextValue: string) => void}
                label={energyUsagePurposeList.value.label}
                hint={hint}
                error={error?.message}
                isOptional={!energyUsagePurposeList.value.requiredMessage}>
                {energyUsagePurposeList.value.options?.map(option => (
                  <RadioButton value={option.name} key={option.name}>
                    {option.label}
                  </RadioButton>
                ))}
              </RadioGroup>
            </div>
          )}
        />
      </Stack>
    </Box>
  );
};

export { EnergyUsagePurposeSelector };
export default EnergyUsagePurposeSelector;
